import { Controller } from "@hotwired/stimulus"

/**
 * Adds and removes location rows on the online dealer form.
 *
 * Before this, the form rendered exactly the persisted rows plus at most one blank,
 * so a second branch cost two save round-trips: save one, reopen, type the next.
 * This clones a <template> of the blank row instead, so a multi-branch dealer is
 * entered in one sitting.
 *
 * The template's fields are rendered with NEW_RECORD as their child index; each clone
 * swaps it for a timestamp so two added rows never share field names. Removal of an
 * unsaved row is just removal from the screen - nothing exists to destroy, and
 * accepts_nested_attributes' reject_if: :all_blank would have discarded an untouched
 * one anyway. Persisted rows keep their _destroy checkbox; that is a save, not a
 * screen edit, and it stays one.
 *
 * A removal announces itself as `location-rows:removed`, bubbling off this element.
 * Removing a row takes its branch off the form without touching a single field, so
 * nothing watching the form for input or change can see it happen; the ranking map
 * measures from these rows and needs to be told.
 *
 * No user-facing copy in here - the row and its buttons are ERB, which is why
 * i18n-tasks is configured to ignore JS paths.
 *
 * Expected markup (see the Locations section of _form.html.erb):
 *   <section data-controller="location-rows">
 *     <div data-location-rows-target="rows">…persisted rows…</div>
 *     <template data-location-rows-target="template">…blank row…</template>
 *     <button type="button" data-action="location-rows#add">
 *   Each row: <div data-location-row>…<button data-action="location-rows#remove"></div>
 */
// Rails keys each nested row by whatever replaces NEW_RECORD, and two rows sharing a key
// are merged into one record on save - the second row's values silently overwrite the
// first's. Date.now() alone collides whenever two rows are added inside the same
// millisecond, so a counter runs alongside it and does the actual uniqueness work; the
// timestamp only keeps a fresh page load clear of the server-rendered indexes, which
// start at 0. Digits only on purpose: this value also lands inside each row's DOM ids
// via field_id, so keeping it numeric leaves those ids exactly the shape they are today.
let rowsAdded = 0
const nextRowIndex = () => `${Date.now()}${String(rowsAdded++).padStart(3, "0")}`

export default class extends Controller {
  static targets = ["rows", "template"]

  add() {
    if (!this.hasRowsTarget || !this.hasTemplateTarget) return

    const html = this.templateTarget.innerHTML.replace(/NEW_RECORD/g, nextRowIndex())
    this.rowsTarget.insertAdjacentHTML("beforeend", html)

    const added = this.rowsTarget.lastElementChild
    const label = added && added.querySelector("input[type='text']")
    if (label) label.focus()
  }

  remove(event) {
    const row = event.target.closest("[data-location-row]")
    if (!row) return

    row.remove()
    // Removing an element fires nothing about the fields that went with it - the same gap
    // location-lookup closes from the other side by dispatching input on the fields it
    // fills programmatically. Bubbling, because the only watcher is on the form rather
    // than on this section. Adding a row needs no such notice: a cloned row is blank, and
    // a branch with no coordinates is not a branch.
    this.dispatch("removed", { bubbles: true })
  }
}
